<template>
    <DsDialog
        v-model:open="isOpen"
        :isPrimaryDestructive="canDelete"
        :primaryLabel="canDelete ? 'Delete file' : 'Close'"
        :secondaryLabel="canDelete ? 'Cancel' : undefined"
        :title="canDelete ? 'Delete file' : 'Cannot delete file'"
        @clickPrimary="handlePrimaryAction"
        @clickSecondary="isOpen = false"
    >
        <p class="mb-4 text-content-lg">
            <template v-if="canDelete">
                This action cannot be undone. The file will be permanently removed, and all contacts associated with it will no
                longer have access.
            </template>
            <template v-else>{{ file.safe_to_delete_reason }}</template>
        </p>
        <DsCard bgColor="bg-ds-gray-50">
            <div class="grid grid-cols-2 gap-3">
                <p>File reference #</p>
                <p class="text-right font-bold">{{ file.ref_number }}</p>
                <p>Property address</p>
                <p class="break-words text-right font-bold">{{ file.address || EMPTY_CELL_CONTENT }}</p>
            </div>
        </DsCard>
    </DsDialog>
</template>

<script setup lang="ts">
import { EMPTY_CELL_CONTENT } from '@/components/Components/static-content';
import DsDialog from '@/components/design-system/DsDialog.vue';
import useDsToast from '@/components/design-system/toast/useDsToast';
import type { DeletableFile } from '@/components/Views/Company/File/file-table.types';
import { useFileDetailStore } from '@/stores/company/file-detail.store';
import DsCard from '@ds/DsCard.vue';
import { computed } from 'vue';

const file = defineModel<DeletableFile>('file', { required: true });
const isOpen = defineModel<boolean>('open');

const emit = defineEmits<{
    (event: 'deleted'): void;
}>();

const canDelete = computed(() => file.value.safe_to_delete);

const { addToast } = useDsToast();

const fileDetailStore = useFileDetailStore();
const { deleteFile } = fileDetailStore.useDeleteFile(() => file.value.routes.delete_file);

const executeDeleteFile = async () => {
    try {
        await deleteFile();
        emit('deleted');
    } catch (error) {
        addToast({
            detail: `Failed to delete file # ${file.value.ref_number}`,
            severity: 'error',
        });
    }
};

const handlePrimaryAction = () => {
    if (!canDelete.value) {
        return (isOpen.value = false);
    }

    return executeDeleteFile();
};
</script>
